import React, { useEffect, useRef, useState } from 'react'
import { UnsavedChangesModal } from '../../../../common/components/UnsavedChangesModal'
import { customSwal } from '../../../../config/swal'
import { BackofficePermissionsEnum as Permissions } from '../../../../common/enums/backOfficePermissionsEnum'
import usePermissions from '../../../../common/hooks/usePermissions'
import { useUnsavedChanges } from '../../../../common/hooks/useUnsavedChanges'
import {
  FundDraftService,
  FundSeriesTab,
  IPortfolioTabItem,
} from '../../../../services/FundDraftService'
import { TabSkeleton } from '../../../../common/components/TabSkeleton'
import { useFundSeries } from '../hooks/useFundSeries'

interface IProps {
  fundId: string
  onDirtyChange?: (dirty: boolean) => void
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}

const emptyItem: IPortfolioTabItem = { name: '', color: 'var(--color-primary-blue)', graphicValue: '' }

export const PortfolioTab: React.FC<IProps> = ({ fundId, onDirtyChange, saveRef }) => {
  const { hasPermission } = usePermissions()
  const { data: seriesData, isLoading, refetchSeriesOnly } = useFundSeries(fundId)

  const [activeSerieCode, setActiveSerieCode] = useState('')
  const [portfolioAction, setPortfolioAction] = useState<'saving' | 'publishing' | null>(null)
  const [visibility, setVisibility] = useState(true)
  const [title, setTitle] = useState('')
  const [items, setItems] = useState<IPortfolioTabItem[]>([emptyItem])
  const [isDirty, setIsDirty] = useState(false)

  const initialized = useRef(false)
  const skipDirtyCount = useRef(1)

  const applySerieData = (code: string) => {
    if (!seriesData) return
    skipDirtyCount.current += 1
    const portfolio = seriesData.tabs.portfolio[code]
    if (!portfolio) {
      setVisibility(true)
      setTitle('')
      setItems([{ ...emptyItem }])
      return
    }
    setVisibility(portfolio.visibility ?? true)
    setTitle(portfolio.title ?? '')
    if (portfolio.data) setItems([...portfolio.data])
  }

  useEffect(() => {
    if (!seriesData) return
    if (initialized.current) {
      if (activeSerieCode) applySerieData(activeSerieCode)
      return
    }
    const firstCode = seriesData.series[0]?.serie_codigo
    if (!firstCode) return
    initialized.current = true
    setActiveSerieCode(firstCode)
    applySerieData(firstCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesData])

  const buildPayload = () => ({ visibility, title, data: items })

  const handleSave = async () => {
    setPortfolioAction('saving')
    try {
      await FundDraftService().updateSeriesTab(
        fundId,
        activeSerieCode,
        FundSeriesTab.PORTFOLIO,
        buildPayload()
      )
      setIsDirty(false)
      refetchSeriesOnly()
      customSwal.fire({ title: 'Portafolio guardado correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al guardar el portafolio', icon: 'error' })
    }
    setPortfolioAction(null)
  }

  const handlePublish = async () => {
    setPortfolioAction('publishing')
    try {
      await FundDraftService().updateSeriesTab(
        fundId,
        activeSerieCode,
        FundSeriesTab.PORTFOLIO,
        buildPayload()
      )
      await FundDraftService().publishSeriesTab(fundId, activeSerieCode, FundSeriesTab.PORTFOLIO)
      setIsDirty(false)
      refetchSeriesOnly()
      customSwal.fire({ title: 'Portafolio publicado correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al publicar el portafolio', icon: 'error' })
    }
    setPortfolioAction(null)
  }

  const { guardNavigation, isModalOpen, handleLeave, handleCancel, handleSaveAndContinue, saving } =
    useUnsavedChanges(isDirty, handleSave)

  const handleSerieChange = (code: string) => {
    guardNavigation(() => {
      setIsDirty(false)
      setActiveSerieCode(code)
      applySerieData(code)
    })
  }

  const addItem = (e: React.MouseEvent) => {
    e.preventDefault()
    setItems((prev) => [...prev, { ...emptyItem }])
  }

  const removeItem = (e: React.MouseEvent, index: number) => {
    e.preventDefault()
    setItems((prev) => prev.filter((_, i) => i !== index))
  }

  // Mark dirty when any user-editable state changes (skip load-triggered fires)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (skipDirtyCount.current > 0) { skipDirtyCount.current -= 1; return }
    setIsDirty(true)
  }, [visibility, title, items])

  // Report dirty state to ConfigFunds
  useEffect(() => {
    onDirtyChange?.(isDirty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty])

  // Keep saveRef current
  useEffect(() => {
    if (saveRef) saveRef.current = handleSave
  })

  if (isLoading && !seriesData) return <TabSkeleton />

  const seriesTabs =
    seriesData && seriesData.series.length > 0 ? (
      <div
        style={{
          display: 'flex',
          borderBottom: '2px solid #f1f3f5',
          marginTop: '12px',
          marginLeft: '-20px',
          marginRight: '-20px',
          paddingLeft: '20px',
        }}
      >
        {seriesData.series.map((serie) => (
          <button
            key={serie.serie_codigo}
            type='button'
            onClick={() => handleSerieChange(serie.serie_codigo)}
            style={{
              border: 'none',
              background: 'none',
              padding: '6px 20px',
              cursor: 'pointer',
              fontWeight: activeSerieCode === serie.serie_codigo ? 600 : 400,
              color: activeSerieCode === serie.serie_codigo ? 'var(--color-primary-blue)' : '#9ca3af',
              borderBottom:
                activeSerieCode === serie.serie_codigo
                  ? '2px solid var(--color-primary-blue)'
                  : '2px solid transparent',
              marginBottom: '-2px',
              fontSize: '14px',
              transition: 'color 0.15s',
            }}
          >
            {serie.serie_name}
          </button>
        ))}
      </div>
    ) : null

  const edicionActivoBadge = seriesData ? (
    <span
      style={{
        background: '#d4edda',
        color: '#155724',
        fontSize: '12px',
        padding: '2px 10px',
        borderRadius: 20,
        fontWeight: 600,
      }}
    >
      Edición activo
    </span>
  ) : null

  return (
    <div>
      <div className='card mt-10 p-5'>
        <div className='d-flex align-items-center justify-content-between mt-2 mb-1'>
          <div className='d-flex align-items-center gap-3'>
            <span className='text-gray-800 fs-2 fw-bold'>Portafolio</span>
            {edicionActivoBadge}
          </div>
        </div>
        {seriesTabs}

        <div className='d-flex align-items-center gap-3 mt-6 mb-3'>
          <label className='fw-bolder fs-6 text-gray-700 mb-0'>Mostrar información</label>
          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={visibility}
              onChange={(e) => setVisibility(e.target.checked)}
            />
          </div>
        </div>
        <label className='fw-bolder fs-5 text-gray-700 mb-3 d-block'>Título</label>
        <textarea
          className='form-control'
          style={{ height: '50px', resize: 'none' }}
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder='Título'
        />
      </div>

      <div className='card mt-10 p-10'>
        <div className='w-full mb-4 d-flex justify-content-between align-items-center'>
          <label className='fw-bolder fs-5 text-gray-700 mb-3'>Información del gráfico</label>
          <button
            style={{ color: 'white', marginRight: '2px', background: 'rgb(0, 123, 195)' }}
            className='btn mr-3'
            type='button'
            onClick={addItem}
            disabled={!visibility || !hasPermission(Permissions.FUND_TABS.PORTFOLIO.GRAPHIC_EDIT)}
          >
            <i style={{ color: 'white' }} className='bi bi-plus mr-2 fs-1' />
            <span>Agregar campo</span>
          </button>
        </div>
        <table className='table table-row-dashed table-row-gray-300 gy-7'>
          <thead>
            <tr className='fw-bolder fs-6 text-gray-700'>
              <th />
              <th>TÍTULO</th>
              <th>VALOR</th>
              <th>COLOR</th>
              <th className='text-center'>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={index}>
                <td>
                  <div className='mt-5'>{index + 1}</div>
                </td>
                <td>
                  <input
                    className='form-control mb-2 mb-md-0'
                    placeholder='Ingrese título'
                    type='text'
                    value={item.name}
                    onChange={(e) => {
                      const updated = [...items]
                      updated[index] = { ...updated[index], name: e.target.value }
                      setItems(updated)
                    }}
                    disabled={!visibility || !hasPermission(Permissions.FUND_TABS.PORTFOLIO.GRAPHIC_EDIT)}
                  />
                </td>
                <td>
                  <input
                    type='number'
                    className='form-control mb-2 mb-md-0'
                    placeholder='Valor'
                    value={item.graphicValue}
                    onChange={(e) => {
                      const updated = [...items]
                      updated[index] = { ...updated[index], graphicValue: e.target.value }
                      setItems(updated)
                    }}
                    disabled={!visibility || !hasPermission(Permissions.FUND_TABS.PORTFOLIO.GRAPHIC_EDIT)}
                  />
                </td>
                <td>
                  <input
                    type='color'
                    style={{ height: '44px' }}
                    className='form-control'
                    value={item.color}
                    onChange={(e) => {
                      const updated = [...items]
                      updated[index] = { ...updated[index], color: e.target.value }
                      setItems(updated)
                    }}
                    disabled={!visibility || !hasPermission(Permissions.FUND_TABS.PORTFOLIO.GRAPHIC_EDIT)}
                  />
                </td>
                <td className='text-center'>
                  <button
                    disabled={!visibility || !hasPermission(Permissions.FUND_TABS.PORTFOLIO.GRAPHIC_EDIT)}
                    style={{ background: 'transparent', color: 'white', border: '1px solid #ccc' }}
                    className='btn btn-icon ml-3'
                    onClick={(e) => removeItem(e, index)}
                  >
                    <i className='bi bi-trash text-muted fs-3' />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className='text-end'>
            <button
              disabled={!visibility || !hasPermission(Permissions.FUND_TABS.PORTFOLIO.GRAPHIC_EDIT)}
              style={{ background: 'var(--color-primary-blue)', color: 'white', marginRight: '2px' }}
              className='btn mr-3'
              onClick={addItem}
            >
              <i style={{ color: 'white' }} className='bi bi-plus fs-1' /> Agregar fila
            </button>
          </div>
        )}
      </div>

      {/* Acciones */}
      {activeSerieCode &&
        hasPermission([
          Permissions.FUND_TABS.PORTFOLIO.SAVE,
          Permissions.FUND_TABS.PORTFOLIO.PUBLISH,
        ]) && (
          <div className='card mt-5 p-5'>
            <div className='d-flex justify-content-end gap-3'>
              {hasPermission(Permissions.FUND_TABS.PORTFOLIO.SAVE) && (
                <button
                  type='button'
                  className='btn btn-custom-action'
                  onClick={handleSave}
                  disabled={portfolioAction !== null}
                >
                  {portfolioAction === 'saving' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Guardando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Guardar cambios'
                  )}
                </button>
              )}
              {hasPermission(Permissions.FUND_TABS.PORTFOLIO.PUBLISH) && (
                <button
                  type='button'
                  className='btn btn-custom-action'
                  onClick={handlePublish}
                  disabled={portfolioAction !== null}
                >
                  {portfolioAction === 'publishing' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Publicando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Publicar'
                  )}
                </button>
              )}
            </div>
          </div>
        )}

      {isModalOpen && (
        <UnsavedChangesModal
          onLeave={handleLeave}
          onCancel={handleCancel}
          onSave={handleSaveAndContinue}
          saving={saving}
        />
      )}
    </div>
  )
}
